import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  HStack,
  Tag,
  TagLabel,
  Text,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";

import { DeleteIcon } from "@chakra-ui/icons";

import { useState, useRef } from "react";
import { settingRepository, Types } from "api/setting";

interface TypeDrawerProps {
  isOpen: boolean;
  onClose: () => void;

  types: Types[];

  isEditMode: boolean;
  editingId: string | null;

  selectedType: string;
  setSelectedType: (value: string) => void;

  selectedValue: string;
  setSelectedValue: (value: string) => void;

  selectedValues: string[];
  setSelectedValues: React.Dispatch<
    React.SetStateAction<string[]>
  >;

  onSave: () => void;
}

const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_SOFT = "#EEEBFA";
const NAVY = "#2D2A5E";

export default function TypeDrawer({
  isOpen,
  onClose,
  types,
  isEditMode,

  selectedType,
  setSelectedType,

  selectedValue,
  setSelectedValue,

  selectedValues,
  setSelectedValues,

  onSave,
}: TypeDrawerProps) {
  const drawerBg = useColorModeValue("white", "#111C44");
  const textColor = useColorModeValue(NAVY, "white");
  const fieldBorder = useColorModeValue("#D5DCE8", "whiteAlpha.300");
  const optionBg = useColorModeValue("white", "#111C44");
  const helperTextColor = useColorModeValue("gray.500", "whiteAlpha.700");
  const [showTypeOptions, setShowTypeOptions] = useState(false);
  const [showValueOptions, setShowValueOptions] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [valueToDelete, setValueToDelete] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const selectedTypeData = types.find(
    (item) =>
      item.type.toLowerCase() ===
      selectedType.toLowerCase()
  );

  const filteredTypes = types.filter((item) =>
    item.type
      .toLowerCase()
      .includes(selectedType.toLowerCase())
  );

  const filteredValues =
    selectedTypeData?.values.filter((value) =>
      value
        .toLowerCase()
        .includes(selectedValue.toLowerCase())
    ) || [];

  const handleSelectType = (type: string) => {
    setSelectedType(type);

    // Clear values when type changes
    setSelectedValue("");
    setSelectedValues([]);

    setShowTypeOptions(false);
    setShowValueOptions(false);
  };

  const handleTypeChange = (
    value: string
  ) => {
    setSelectedType(value);
    setSelectedValue("");
    setSelectedValues([]);

    setShowTypeOptions(true);
  };

  const handleSelectValue = (
    value: string
  ) => {
    if (!value) return;

    setSelectedValue(value);

    if (!selectedValues.includes(value)) {
      setSelectedValues((prev) => [
        ...prev,
        value,
      ]);
    }

    setSelectedValue("");
    setShowValueOptions(false);
  };

  const handleValueChange = (
    value: string
  ) => {
    setSelectedValue(value);
    setShowValueOptions(true);
  };

  const handleAddManualValue = () => {
    const value = selectedValue.trim();

    if (!value) return;

    if (!selectedValues.includes(value)) {
      setSelectedValues((prev) => [
        ...prev,
        value,
      ]);
    }

    setSelectedValue("");
    setShowValueOptions(false);
  };

  const confirmDeleteValue = async () => {
    if (!valueToDelete) return;

    try {
      const selectedTypeData = types.find(
        (item) =>
          item.type.toLowerCase() ===
          selectedType.toLowerCase()
      );

      if (!selectedTypeData) {
        return;
      }

      await settingRepository.deleteValue(
        selectedTypeData.id, valueToDelete
      );

      setSelectedValues((prev) =>
        prev.filter((item) => item !== valueToDelete)
      );

      setIsDeleteOpen(false);
      setValueToDelete(null);
    } catch (error) {
      console.error("Failed to delete value:", error);
    }
  };

  const handleRemoveValue = (value: string) => {
    setValueToDelete(value);
    setIsDeleteOpen(true);
  };

  const handleSave = () => {
    const type = selectedType.trim();

    if (!type) {
      return;
    }

    const manualValue =
      selectedValue.trim();

    if (
      manualValue &&
      !selectedValues.includes(manualValue)
    ) {
      setSelectedValues((prev) => [
        ...prev,
        manualValue,
      ]);
    }

    onSave();
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
    >
      <DrawerOverlay bg="rgba(0, 0, 0, 0.35)" />

      <DrawerContent
        bg={drawerBg}
        borderRadius="20px 0 0 20px"
        overflow="hidden"
      >

        <DrawerHeader
          bg={`linear-gradient(135deg, ${PURPLE_DARK} 0%, ${PURPLE} 100%)`}
          color="white"
          px={6}
          py={7}
          minH="125px"
          position="relative"
        >
          <Text fontSize="30px" fontWeight="700" lineHeight="1.2" mb={2}>
            {isEditMode ? "Update Type & Values" : "Add Type & Values"}
          </Text>
          <Text fontSize="16px" fontWeight="400" opacity={0.95}>
            Manage types and their available values
          </Text>
          <DrawerCloseButton
            position="absolute"
            right="18px"
            top="22px"
            size="lg"
            color="white"
            fontSize="18px"
            _hover={{ bg: "rgba(255,255,255,0.15)" }}
          />
        </DrawerHeader>

        <DrawerBody px={6} py={7} overflowY="auto">


          <FormControl mb={7}>
            <FormLabel
              fontSize="sm"
              fontWeight="700"
              color={textColor}
              mb="8px"
            >
              Type
            </FormLabel>

            <Box position="relative">
              <Input
                placeholder={isEditMode ? "" : "Select type"}
                value={selectedType}
                readOnly
                onClick={() =>
                  setShowTypeOptions((prev) => !prev)
                }
                pr="45px"
                h="56px"
                borderRadius="14px"
                borderColor={fieldBorder}
                color={textColor}
                bg={drawerBg}
                cursor="pointer"
                _hover={{ borderColor: "#B8C2D1" }}
                _focus={{
                  borderColor: PURPLE,
                  boxShadow: `0 0 0 1px ${PURPLE}`,
                }}
              />

              {/* Arrow */}
              <Box
                position="absolute"
                right="0"
                top="0"
                h="56px"
                w="45px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                color="gray.500"
                onClick={() =>
                  setShowTypeOptions((prev) => !prev)
                }
              >
                ▼
              </Box>

              {/* Type Dropdown */}
              {showTypeOptions && (
                <Box
                  position="absolute"
                  top="60px"
                  left="0"
                  right="0"
                  bg={optionBg}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="12px"
                  boxShadow="lg"
                  zIndex={100}
                  maxH="200px"
                  overflowY="auto"
                >
                  {types.map((item) => (
                    <Box
                      key={item.id}
                      px={4}
                      py={3}
                      cursor="pointer"
                      _hover={{
                        bg: PURPLE_SOFT,
                      }}
                      onClick={() => {
                        setSelectedType(item.type);
                        // setShowTypeOptions(false);
                      }}

                    >
                      {item.type}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Text
              mt={2}
              fontSize="11px"
              color={helperTextColor}
            >
              Select a type from the available options.
            </Text>
          </FormControl>
          <FormControl mb={6}>
            <FormLabel
              fontSize="sm"
              fontWeight="700"
              color={textColor}
              mb="8px"
            >
              Values
            </FormLabel>

            <Box position="relative">
              <Input
                placeholder={isEditMode ? "" : "Enter or select value"}
                value={selectedValue}
                onChange={(e) =>
                  handleValueChange(
                    e.target.value
                  )
                }
                onFocus={() =>
                  setShowValueOptions(true)
                }
                pr="45px"
                h="56px"
                borderRadius="14px"
                borderColor={fieldBorder}
                color={textColor}
                bg={drawerBg}
                _hover={{ borderColor: "#B8C2D1" }}
                _focus={{
                  borderColor: PURPLE,
                  boxShadow: `0 0 0 1px ${PURPLE}`,
                }}
              />

              {/* Arrow */}
              <Box
                position="absolute"
                right="0"
                top="0"
                h="56px"
                w="45px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                color="gray.500"
                onClick={() =>
                  setShowValueOptions(
                    (prev) => !prev
                  )
                }
              >

              </Box>

              {/* Value Dropdown */}
              {showValueOptions && (
                <Box
                  position="absolute"
                  top="50px"
                  left="0"
                  right="0"
                  bg={optionBg}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="12px"
                  boxShadow="lg"
                  zIndex={99}
                  maxH="200px"
                  overflowY="auto"
                >

                  <Box
                    px={4}
                    py={3}
                    color={PURPLE}
                  >
                    {selectedValue
                      ? `Use "${selectedValue}"`
                      : "Enter a new value"}
                  </Box>

                  {/* Add manually typed value */}
                  {selectedValue &&
                    !filteredValues.includes(
                      selectedValue
                    ) && (
                      <Box
                        px={4}
                        py={3}
                        cursor="pointer"
                        color={PURPLE}
                        fontWeight="600"
                        borderTop="1px solid"
                        borderColor="gray.100"
                        onClick={
                          handleAddManualValue
                        }
                      >
                        + Add "
                        {selectedValue}"
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          </FormControl>
          {selectedValues.length > 0 && (
            <Box mt={6}>
              <FormLabel
                fontSize="sm"
                fontWeight="600"
                color={textColor}
              >
                Selected Values
              </FormLabel>

              <Box>
                {selectedValues.map((value) => (
                  <HStack
                    key={value}
                    justifyContent="space-between"
                    px={4}
                    py={3}
                    mb={2}
                    border="1px solid"
                    borderColor={fieldBorder}
                    borderRadius="12px"
                    bg={drawerBg}
                  >
                    <Text
                      fontSize="sm"
                      color={textColor}
                    >
                      {value}
                    </Text>

                    <DeleteIcon
                      boxSize={4}
                      color="red.500"
                      cursor="pointer"
                      _hover={{
                        color: "red.600",
                      }}
                      onClick={() =>
                        handleRemoveValue(value)
                      }
                    />
                  </HStack>
                ))}
              </Box>
            </Box>
          )}
        </DrawerBody>
        <DrawerFooter
          bg={drawerBg}
          borderTop="1px solid"
          borderColor={fieldBorder}
          px={6}
          py={5}
          gap={4}
          justifyContent="flex-end"
        >
          <Button
            h="50px"
            px={6}
            variant="outline"
            borderColor={PURPLE}
            color={PURPLE}
            borderRadius="14px"
            onClick={onClose}
            _hover={{ bg: PURPLE_SOFT }}
          >
            Cancel
          </Button>

          <Button
            h="50px"
            px={7}
            bg={PURPLE}
            color="white"
            borderRadius="14px"
            onClick={handleSave}
            _hover={{
              bg: PURPLE_DARK,
            }}
          >
            {isEditMode
              ? "Update"
              : "Save"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setIsDeleteOpen(false);
          setValueToDelete(null);
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            maxW="400px"
            borderRadius="16px"
            mx={4}
          >
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="700"
            >
              Delete Value
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <Text as="span" fontWeight="700">
                "{valueToDelete}"
              </Text>
              ?

              <Text mt={2} fontSize="sm" color="gray.500">
                This action cannot be undone.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter gap={3}>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setIsDeleteOpen(false);
                  setValueToDelete(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>

              <Button
                colorScheme="red"
                onClick={confirmDeleteValue}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Drawer>
  );
}